import { useState } from "react";
import "./InvoiceDetails.css";

import {
    MapPin,
    Clock3,
    MessageSquare,
    CreditCard,
    Pencil,
    Wallet,
    Landmark,
    ChevronDown,
} from "lucide-react";

import { usePayment } from "../../../../../context/PaymentContext";

export default function InvoiceDetails({
    address,
    shipping,
    comment,
    onEditAddress,
    onEditShipping,
}) {
    const [isOpen, setIsOpen] = useState(false);

    const {
        paymentMethod,
        selectedGateway,
        useWallet,
        finalSummary,
    } = usePayment();

    const renderShippingTime = () => {
        if (!shipping) {
            return "-";
        }

        if (shipping.displayText) {
            return shipping.displayText;
        }

        switch (shipping.method || shipping.type) {
            case "normal-post":
            case "peyk":
                if (!shipping.schedule) {
                    return "زمان ارسال انتخاب نشده";
                }

                return (
                    <>
                        {shipping.schedule.date}
                        {" | "}
                        {String(
                            shipping.schedule.time_start ??
                            shipping.schedule.timeStart ??
                            ""
                        ).padStart(2, "0")}
                        :00
                        {" - "}
                        {String(
                            shipping.schedule.time_end ??
                            shipping.schedule.timeEnd ??
                            ""
                        ).padStart(2, "0")}
                        :00
                    </>
                );

            case "express-post":
                return "ارسال فوری";

            case "post":
                return "ارسال توسط پست";

            case "tipax":
                return "ارسال توسط تیپاکس";

            case "attended-delivery":
            case "pickup":
                return "تحویل حضوری";

            default:
                return "-";
        }
    };

    const getPaymentMethodLabel = () => {
        if (paymentMethod === "online") {
            return "پرداخت آنلاین";
        }

        if (paymentMethod === "cash") {
            return "پرداخت در محل";
        }

        return "-";
    };

    const walletUsed = Number(
        finalSummary?.walletUsed || 0
    );

    const payable = Number(
        finalSummary?.payable || 0
    );

    return (
        <section
            className={`invoice-card ${
                isOpen ? "open" : "closed"
            }`}
            dir="rtl"
        >
            {/* HEADER / TOGGLE */}
            <button
                type="button"
                className="invoice-toggle"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
            >
                <div className="invoice-toggle-left">
                    <div className="invoice-toggle-icon">
                        <CreditCard size={20} />
                    </div>

                    <div>
                        <h3>اطلاعات سفارش</h3>

                        <span>
                            مشاهده جزئیات آدرس، ارسال و پرداخت
                        </span>
                    </div>
                </div>

                <ChevronDown
                    className={`invoice-chevron ${
                        isOpen ? "rotated" : ""
                    }`}
                    size={22}
                />
            </button>

            {/* CONTENT */}
            <div
                className={`invoice-content-wrapper ${
                    isOpen ? "visible" : ""
                }`}
            >
                <div className="invoice-content-inner">

                    {/* ADDRESS */}
                    <div className="invoice-item">
                        <div className="invoice-icon">
                            <MapPin size={20} />
                        </div>

                        <div className="invoice-content">
                            <label>
                                آدرس تحویل
                            </label>

                            <p>
                                {address?.province &&
                                    `${address.province} - `}
                                {address?.city &&
                                    `${address.city} - `}
                                {address?.address || "-"}
                            </p>
                        </div>

                        <button
                            type="button"
                            className="invoice-edit"
                            onClick={(event) => {
                                event.stopPropagation();
                                onEditAddress?.();
                            }}
                            aria-label="تغییر آدرس"
                        >
                            <Pencil size={16} />
                        </button>
                    </div>

                    {/* SHIPPING */}
                    <div className="invoice-item">
                        <div className="invoice-icon">
                            <Clock3 size={20} />
                        </div>

                        <div className="invoice-content">
                            <label>
                                زمان / روش ارسال
                            </label>

                            <p>
                                {renderShippingTime()}
                            </p>
                        </div>

                        <button
                            type="button"
                            className="invoice-edit"
                            onClick={(event) => {
                                event.stopPropagation();
                                onEditShipping?.();
                            }}
                            aria-label="تغییر روش ارسال"
                        >
                            <Pencil size={16} />
                        </button>
                    </div>

                    {/* COMMENT */}
                    <div className="invoice-item">
                        <div className="invoice-icon">
                            <MessageSquare size={20} />
                        </div>

                        <div className="invoice-content">
                            <label>
                                توضیحات سفارش
                            </label>

                            <p>
                                {comment?.trim()
                                    ? comment
                                    : "بدون توضیحات"}
                            </p>
                        </div>
                    </div>

                    {/* PAYMENT */}
                    <div className="invoice-item">
                        <div className="invoice-icon">
                            <CreditCard size={20} />
                        </div>

                        <div className="invoice-content">
                            <label>
                                روش پرداخت
                            </label>

                            <p>
                                {getPaymentMethodLabel()}
                            </p>

                            {paymentMethod === "online" &&
                                selectedGateway?.name && (
                                    <span className="invoice-subtext">
                                        درگاه:{" "}
                                        {selectedGateway.name}
                                    </span>
                                )}
                        </div>
                    </div>

                    {/* WALLET */}
                    {useWallet && walletUsed > 0 && (
                        <div className="invoice-item">
                            <div className="invoice-icon wallet">
                                <Wallet size={20} />
                            </div>

                            <div className="invoice-content">
                                <label>
                                    پرداخت از کیف پول
                                </label>

                                <p className="invoice-success">
                                    {walletUsed.toLocaleString(
                                        "fa-IR"
                                    )}{" "}
                                    تومان
                                </p>
                            </div>
                        </div>
                    )}

                    {/* FINAL */}
                    <div className="invoice-item invoice-final-item">
                        <div className="invoice-icon payment">
                            <Landmark size={20} />
                        </div>

                        <div className="invoice-content">
                            <label>
                                مبلغ قابل پرداخت
                            </label>

                            <p className="invoice-total">
                                {payable.toLocaleString(
                                    "fa-IR"
                                )}{" "}
                                تومان
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}